import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/lib/utils';

const toggleGroupItemVariants = cva(
  'focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'text-sm font-medium text-ebony_opacity_80 capitalize [&[data-state=on]]:pointer-events-none border py-2.5 rounded-sm-5 [&[data-state=on]]:border-[#a498ff80] hover:border-[#a498ff80] border-[#E3E6E8] bg-white [&[data-state=on]]:bg-[#a498ff1a]',
        // Filter chips: grey when off, purple when on; toggled on and off by clicking.
        chip: 'inline-flex items-center gap-1 whitespace-nowrap border border-grey_300 bg-light/40 font-medium text-tertiary transition-colors hover:border-purple_600 focus-visible:ring-2 focus-visible:ring-purple_600 focus-visible:ring-offset-1 data-[state=on]:border-purple_600 data-[state=on]:bg-purple_150/50 data-[state=on]:text-purple_750',
        // One half of a two-option switch; the group lays the halves side by side.
        segment:
          'whitespace-nowrap border border-grey_300 px-2 py-1.5 text-[9px] font-bold uppercase leading-normal tracking-[0.5px] text-grey_450 transition-colors first:rounded-l-lg first:border-r-0 last:rounded-r-lg hover:text-purple_600 focus-visible:ring-2 focus-visible:ring-purple_600 data-[state=on]:bg-purple_150/70 data-[state=on]:text-purple_600',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const ToggleGroup = React.forwardRef<
  React.ComponentRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <ToggleGroupPrimitive.Root className={cn('', className)} {...props} ref={ref} />;
});
ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ComponentRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleGroupItemVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(toggleGroupItemVariants({ variant }), className)}
      {...props}
    />
  );
});
ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem };
